/**
 * O push com o CRM fechado, do lado do navegador (#536, ADR-0017).
 *
 * ── As três peças ────────────────────────────────────────────────────────
 *
 *   1. O service worker (`public/sw-notificacoes.js`): fica registrado no
 *      navegador mesmo sem aba aberta, e é quem mostra o aviso quando o push
 *      chega.
 *   2. A inscrição (`PushSubscription`): o endereço e as chaves que o
 *      navegador cria a partir da nossa chave VAPID pública.
 *   3. O registro no servidor (`POST /v1/me/dispositivos`): liga a inscrição a
 *      quem está logado.
 *
 * ── A verdade é a inscrição do navegador ─────────────────────────────────
 *
 * "Ligado" é existir inscrição NESTE navegador. O servidor pode ter perdido a
 * linha (inscrição morta apagada, chave trocada, banco recriado): por isso,
 * ao entrar, a inscrição existente é registrada de novo — o registro é upsert
 * e não duplica (`sincronizarPush`).
 */

import type { ClienteDaApi } from '@cliqcrm/contracts';
import { pedirPermissaoDoAviso, permissaoDoAviso } from '../model/aviso-do-sistema';

export const CAMINHO_DO_SERVICE_WORKER = '/sw-notificacoes.js';

export type SituacaoDoPush =
  /** Navegador sem service worker ou sem Push API. */
  | 'indisponivel'
  /** O servidor não tem chave VAPID: push desligado lá. */
  | 'sem-servidor'
  /** A pessoa, ou o navegador, negou as notificações. */
  | 'bloqueado'
  | 'desligado'
  | 'ligado';

export function suportaPush(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

/** A chave VAPID vem em base64url; o `subscribe` quer os bytes. */
export function bytesDaChave(base64url: string): Uint8Array<ArrayBuffer> {
  const preenchido = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const base64 = preenchido + '='.repeat((4 - (preenchido.length % 4)) % 4);
  const binario = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binario.length));
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

/** A inscrição foi feita com esta chave? Se a chave do servidor mudou, ela está morta. */
function mesmaChave(inscricao: PushSubscription, chave: Uint8Array): boolean {
  const atual = inscricao.options.applicationServerKey;
  if (!atual) return false;
  const bytes = new Uint8Array(atual);
  return bytes.length === chave.length && bytes.every((b, i) => b === chave[i]);
}

/** "Chrome no macOS" — o que a pessoa reconhece se um dia listar os aparelhos. */
export function rotuloDoNavegador(agente = navigator.userAgent): string {
  const navegador = /Edg\//.test(agente)
    ? 'Edge'
    : /Firefox\//.test(agente)
      ? 'Firefox'
      : /Chrome\//.test(agente)
        ? 'Chrome'
        : /Safari\//.test(agente)
          ? 'Safari'
          : 'Navegador';
  const sistema = /Mac OS X|Macintosh/.test(agente)
    ? 'macOS'
    : /Windows/.test(agente)
      ? 'Windows'
      : /Android/.test(agente)
        ? 'Android'
        : /iPhone|iPad/.test(agente)
          ? 'iOS'
          : /Linux/.test(agente)
            ? 'Linux'
            : 'sistema desconhecido';
  return `${navegador} no ${sistema}`;
}

async function registroDoServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration(CAMINHO_DO_SERVICE_WORKER);
}

export async function inscricaoAtual(): Promise<PushSubscription | null> {
  if (!suportaPush()) return null;
  const registro = await registroDoServiceWorker();
  return registro ? registro.pushManager.getSubscription() : null;
}

async function registrarNoServidor(api: ClienteDaApi, inscricao: PushSubscription): Promise<void> {
  const json = inscricao.toJSON();
  if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys['auth']) return;
  await api.registrarDispositivo({
    corpo: {
      plataforma: 'web',
      endpoint: json.endpoint,
      chaves: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
      rotulo: rotuloDoNavegador(),
    },
  });
}

/** Onde o push está, para o interruptor dizer a verdade. */
export async function situacaoDoPush(api: ClienteDaApi): Promise<SituacaoDoPush> {
  if (!suportaPush()) return 'indisponivel';
  const { chave_publica } = await api.chavePublicaDoPush({});
  if (!chave_publica) return 'sem-servidor';
  if (permissaoDoAviso() === 'denied') return 'bloqueado';
  const inscricao = await inscricaoAtual();
  return inscricao && mesmaChave(inscricao, bytesDaChave(chave_publica)) ? 'ligado' : 'desligado';
}

/**
 * Liga — é um gesto: pede a permissão se ainda não houver, registra o service
 * worker, cria a inscrição e a guarda no servidor.
 */
export async function ligarPush(api: ClienteDaApi): Promise<SituacaoDoPush> {
  if (!suportaPush()) return 'indisponivel';
  const { chave_publica } = await api.chavePublicaDoPush({});
  if (!chave_publica) return 'sem-servidor';

  if (permissaoDoAviso() === 'default') await pedirPermissaoDoAviso();
  if (permissaoDoAviso() !== 'granted') return 'bloqueado';

  const chave = bytesDaChave(chave_publica);
  await navigator.serviceWorker.register(CAMINHO_DO_SERVICE_WORKER, { scope: '/' });
  const registro = await navigator.serviceWorker.ready;

  let inscricao = await registro.pushManager.getSubscription();
  // Inscrição feita com uma chave antiga não recebe mais nada: refaz.
  if (inscricao && !mesmaChave(inscricao, chave)) {
    await inscricao.unsubscribe();
    inscricao = null;
  }
  inscricao ??= await registro.pushManager.subscribe({
    // Exigido pelos navegadores: todo push mostra um aviso visível.
    userVisibleOnly: true,
    applicationServerKey: chave,
  });

  await registrarNoServidor(api, inscricao);
  return 'ligado';
}

/** Desliga NESTE navegador: tira do servidor e cancela a inscrição. */
export async function desligarPush(api: ClienteDaApi | null): Promise<void> {
  const inscricao = await inscricaoAtual();
  if (!inscricao) return;
  // Servidor primeiro: se cair no meio, o pior é uma linha que o despachante
  // apaga no primeiro 410 — e não um navegador inscrito que ninguém vê.
  await api?.removerDispositivo({ corpo: { endpoint: inscricao.endpoint } }).catch(() => undefined);
  await inscricao.unsubscribe().catch(() => false);
}

/**
 * Ao entrar: se este navegador já tem inscrição, garante que o servidor a
 * conhece. Silencioso — é manutenção, não pedido: nunca pede permissão.
 */
export async function sincronizarPush(api: ClienteDaApi): Promise<void> {
  if (!suportaPush() || permissaoDoAviso() !== 'granted') return;
  const inscricao = await inscricaoAtual();
  if (!inscricao) return;
  const { chave_publica } = await api.chavePublicaDoPush({});
  if (!chave_publica || !mesmaChave(inscricao, bytesDaChave(chave_publica))) return;
  await registrarNoServidor(api, inscricao);
}
